// Thin fetch wrappers around the confirmations API. Both functions always
// resolve (never throw) — callers inspect `.success` + `.status` and map to
// a UI state. This keeps the hook's state-machine straightforward.

import type {
  ChatIdentity,
  ConfirmAction,
  DidNotAttendDetail,
  DidNotAttendReason,
  ResolveResponse,
} from '../types';

const BASE = import.meta.env.VITE_API_URL || '';

export type ResolveCode = 'SUPERSEDED';

/** Failure branch of `resolveToken`.
 *
 * `identificationEmail` / `identificationToken` are present on the
 * EXPIRED-with-grace 410 path — the server still mints a chat visitor-ID
 * JWT when the token is past `expires_at` but inside the 30-day grace
 * window, so the chat widget can authenticate the veteran even though the
 * booking-confirmation flow is dead. Both fields appear together or not
 * at all. Absent on SUPERSEDED, on past-grace EXPIRED, and on every
 * non-410 failure. */
export type ResolveError = {
  success: false;
  status: number;
  code?: ResolveCode;
  identificationEmail?: string;
  identificationToken?: string;
};

export type ResolveResult = ResolveResponse | ResolveError;

export async function resolveToken(token: string): Promise<ResolveResult> {
  try {
    const res = await fetch(`${BASE}/api/confirmations/${token}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      // 410 may carry { code: 'SUPERSEDED' } to distinguish a cascade-killed
      // token ("you're all up to date") from a naturally expired one. It
      // may also carry { identificationEmail, identificationToken } on the
      // EXPIRED-with-grace path — the server proves contact identity from
      // the still-recent token row even though the booking flow is dead.
      if (res.status === 410) {
        try {
          const body = (await res.json()) as {
            code?: string;
            identificationEmail?: string;
            identificationToken?: string;
          };
          const code = body.code === 'SUPERSEDED' ? body.code : undefined;
          const identificationEmail =
            typeof body.identificationEmail === 'string' ? body.identificationEmail : undefined;
          const identificationToken =
            typeof body.identificationToken === 'string' ? body.identificationToken : undefined;
          return {
            success: false,
            status: 410,
            code,
            ...(identificationEmail && identificationToken
              ? { identificationEmail, identificationToken }
              : {}),
          };
        } catch {
          return { success: false, status: 410 };
        }
      }
      return { success: false, status: res.status };
    }
    return (await res.json()) as ResolveResponse;
  } catch {
    // Network error — treat as 5xx for state-machine purposes.
    return { success: false, status: 0 };
  }
}

export type SubmitCode = 'ALREADY_DONE' | 'CANCELLED' | 'SUPERSEDED';

/**
 * POST the user's decision. Parses the 409 body to surface the discriminator
 * the server sends — ALREADY_DONE (booking was confirmed by staff since mint)
 * vs CANCELLED (booking was cancelled since mint). Both are 409; only `code`
 * lets the hook pick the right screen.
 */
export async function submitConfirmation(
  token: string,
  bookingId: string,
  action: ConfirmAction,
  reason?: DidNotAttendReason | null,
  detail?: DidNotAttendDetail | null,
): Promise<{ success: true; status: number } | { success: false; status: number; code?: SubmitCode }> {
  try {
    const res = await fetch(`${BASE}/api/confirmations/${token}/confirm`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // Only spread reason/detail when actually present so a plain confirm
      // stays a bare { bookingId, action } body (matches the legacy wire and
      // the server's "reject any reason payload on confirmed" guard).
      body: JSON.stringify({
        bookingId,
        action,
        ...(reason ? { reason } : {}),
        ...(detail ? { detail } : {}),
      }),
    });
    if (res.ok) return { success: true, status: res.status };

    // 409: server sends { code: 'ALREADY_DONE' | 'CANCELLED' }.
    // 410: server sends { code: 'SUPERSEDED' } when the cascade fired, or no
    //      code for a naturally expired token. Parse defensively for both.
    if (res.status === 409 || res.status === 410) {
      try {
        const body = (await res.json()) as { code?: string };
        const validCodes = ['ALREADY_DONE', 'CANCELLED', 'SUPERSEDED'] as const;
        const code = (validCodes as readonly string[]).includes(body.code ?? '')
          ? (body.code as SubmitCode)
          : undefined;
        return { success: false, status: res.status, code };
      } catch {
        return { success: false, status: res.status };
      }
    }

    return { success: false, status: res.status };
  } catch {
    return { success: false, status: 0 };
  }
}

/**
 * Read the cookie-based chat-identity session. Returns `{ identified: false }`
 * on any failure (network, 5xx, malformed payload) — chat is always
 * best-effort and never blocks the page from rendering.
 */
export async function fetchChatIdentity(): Promise<ChatIdentity> {
  try {
    const res = await fetch(`${BASE}/api/chat-identity`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) return { identified: false };
    const json = (await res.json()) as unknown;
    if (
      typeof json === 'object' && json !== null &&
      'identified' in json && (json as { identified: unknown }).identified === true &&
      typeof (json as Record<string, unknown>).identificationEmail === 'string' &&
      typeof (json as Record<string, unknown>).identificationToken === 'string'
    ) {
      return {
        identified: true,
        identificationEmail: (json as Record<string, string>).identificationEmail,
        identificationToken: (json as Record<string, string>).identificationToken,
      };
    }
    return { identified: false };
  } catch {
    return { identified: false };
  }
}
